import { useState, useEffect, useRef } from "react";
import { api, fileUrl, backendUrl } from "../../lib/api";
import { FONTS, STOCK_IMAGES, newTextElement, newShapeElement, newImageElement, newVideoElement, newEmbedElement, parseEmbed } from "../../lib/constants";
import { useFonts } from "../../context/FontsContext";
import { useSubscription } from "../../context/SubscriptionContext";
import { toast } from "sonner";
import {
  Square, Circle, Minus, MagicWand, UploadSimple, Image as ImageIcon,
  TextT, Spinner, Plus, Trash, Sparkle, FilmSlate, Play, Crown, MusicNotes, LinkSimple,
} from "@phosphor-icons/react";
import { Button } from "../ui/button";
import DriveImport from "./DriveImport";

const SectionLabel = ({ children }) => (
  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-3">{children}</p>
);

export function TemplatesPanel({ templates, onApply }) {
  return (
    <div className="p-4 custom-scrollbar overflow-y-auto h-full">
      <SectionLabel>Flyer & poster templates</SectionLabel>
      <div className="grid grid-cols-2 gap-3">
        {templates.map((t) => (
          <button
            key={t.id}
            onClick={() => onApply(t)}
            data-testid={`panel-template-${t.id}`}
            className="group rounded-lg overflow-hidden border border-white/10 hover:border-blue-500/60 transition-all relative"
            style={{ aspectRatio: `${t.width}/${t.height}` }}
          >
            <img src={t.thumbnail} alt={t.title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <span className="text-xs font-semibold">Apply</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export function ElementsPanel({ onAdd }) {
  const shapes = [
    { type: "rect", icon: Square, label: "Rectangle" },
    { type: "ellipse", icon: Circle, label: "Circle" },
    { type: "line", icon: Minus, label: "Line" },
  ];
  return (
    <div className="p-4 custom-scrollbar overflow-y-auto h-full">
      <SectionLabel>Shapes</SectionLabel>
      <div className="grid grid-cols-3 gap-3 mb-6">
        {shapes.map((s) => (
          <button
            key={s.type}
            onClick={() => onAdd(newShapeElement(s.type))}
            data-testid={`add-shape-${s.type}`}
            className="aspect-square rounded-lg border border-white/10 bg-[#0E0E0E] hover:bg-[#1a1a1a] hover:border-blue-500/40 flex flex-col items-center justify-center gap-1.5 transition-all"
          >
            <s.icon weight="fill" size={26} className="text-zinc-200" />
            <span className="text-[10px] text-zinc-400">{s.label}</span>
          </button>
        ))}
      </div>
      <SectionLabel>Stock photos</SectionLabel>
      <div className="grid grid-cols-2 gap-3">
        {STOCK_IMAGES.map((src, i) => (
          <button
            key={i}
            onClick={() => onAdd(newImageElement(src))}
            data-testid={`add-stock-${i}`}
            className="aspect-square rounded-lg overflow-hidden border border-white/10 hover:border-blue-500/60 transition-all"
          >
            <img src={src} alt="stock" className="w-full h-full object-cover" />
          </button>
        ))}
      </div>
    </div>
  );
}

export function OverlaysPanel({ onAdd }) {
  const [overlays, setOverlays] = useState([]);
  useEffect(() => {
    api.get("/overlays").then((r) => setOverlays(r.data)).catch(() => {});
  }, []);

  const groups = overlays.reduce((acc, o) => {
    const c = o.category || "Overlays";
    (acc[c] = acc[c] || []).push(o);
    return acc;
  }, {});
  const order = ["Celebration", "Neon Lights", "Arrows", "Geometric", "Basic Shapes", "Pulsating Lights", "DJ & Party"];
  const cats = Object.keys(groups).sort((a, b) => {
    const ia = order.indexOf(a), ib = order.indexOf(b);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });

  return (
    <div className="p-4 custom-scrollbar overflow-y-auto h-full">
      <p className="text-[11px] text-zinc-500 mb-4 leading-relaxed">
        Animated transparent WebM layers that play over your design. Drop one on, then resize to fit.
      </p>
      {overlays.length === 0 && (
        <div className="text-center text-zinc-500 text-sm py-8">
          <FilmSlate size={32} className="mx-auto mb-2 opacity-50" />
          Loading overlays…
        </div>
      )}
      {cats.map((cat) => (
        <div key={cat} className="mb-6">
          <SectionLabel>{cat}</SectionLabel>
          <div className="grid grid-cols-2 gap-3">
            {groups[cat].map((o) => (
              <button
                key={o.id}
                onClick={() => onAdd(newVideoElement(backendUrl(o.video_url)))}
                data-testid={`overlay-${o.id}`}
                className="group relative rounded-lg overflow-hidden border border-white/10 hover:border-blue-500/60 transition-all aspect-square bg-[#0E0E0E]"
                title={o.name}
              >
                <img src={backendUrl(o.thumb_url)} alt={o.name} loading="lazy" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/45 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Play weight="fill" size={22} />
                </div>
                <span className="absolute bottom-1 left-1.5 right-1.5 text-[10px] font-medium text-white/90 truncate text-left drop-shadow">{o.name}</span>
              </button>
            ))}
          </div>
        </div>
      ))}
      <p className="text-[10px] text-zinc-500 mt-1 leading-relaxed">
        Want more? Upload your own transparent WebM files in the Uploads tab — from your computer or Google Drive.
      </p>
    </div>
  );
}

export function TextPanel({ onAdd }) {
  const { fonts, families, upload, remove } = useFonts();
  const { openUpgrade } = useSubscription();
  const fontInputRef = useRef(null);
  const [uploadingFont, setUploadingFont] = useState(false);
  const presets = [
    { label: "Add a heading", opts: { text: "Heading", fontSize: 96, fontWeight: "800", height: 130 } },
    { label: "Add a subheading", opts: { text: "Subheading", fontSize: 56, fontWeight: "600", height: 80 } },
    { label: "Add body text", opts: { text: "A little bit of body text", fontSize: 32, fontWeight: "400", height: 50 } },
  ];

  const handleFont = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFont(true);
    try {
      const f = await upload(file);
      toast.success(`Font "${f.family}" added`);
    } catch (err) {
      if (err.response?.status === 402) {
        openUpgrade(err.response?.data?.detail || "Custom font upload is a Pro feature.");
      } else {
        toast.error(err.response?.data?.detail || "Font upload failed");
      }
    } finally {
      setUploadingFont(false);
      if (fontInputRef.current) fontInputRef.current.value = "";
    }
  };

  const customFonts = [...FONTS, ...families.filter((f) => !FONTS.includes(f))];

  return (
    <div className="p-4 custom-scrollbar overflow-y-auto h-full">
      <SectionLabel>Add text</SectionLabel>
      <div className="space-y-3 mb-6">
        {presets.map((p, i) => (
          <button
            key={i}
            onClick={() => onAdd(newTextElement(p.opts))}
            data-testid={`add-text-${i}`}
            className="w-full text-left px-4 py-3 rounded-lg border border-white/10 bg-[#0E0E0E] hover:bg-[#1a1a1a] hover:border-blue-500/40 transition-all"
          >
            <span style={{ fontWeight: p.opts.fontWeight, fontSize: Math.min(22, p.opts.fontSize / 4 + 10) }} className="font-display">
              {p.label}
            </span>
          </button>
        ))}
      </div>

      <SectionLabel>Your fonts</SectionLabel>
      <input ref={fontInputRef} type="file" accept=".ttf,.otf,.woff,.woff2,font/ttf,font/otf,font/woff,font/woff2" className="hidden" onChange={handleFont} data-testid="font-upload-input" />
      <Button
        onClick={() => fontInputRef.current?.click()}
        disabled={uploadingFont}
        data-testid="upload-font-btn"
        className="w-full mb-3 bg-blue-600 hover:bg-blue-500 text-white"
      >
        {uploadingFont ? <Spinner className="animate-spin mr-2" size={18} /> : <UploadSimple weight="bold" size={18} className="mr-2" />}
        Upload your own font
      </Button>
      {fonts.length > 0 && (
        <div className="space-y-2 mb-6">
          {fonts.map((f) => (
            <div key={f.id} data-testid={`custom-font-${f.id}`} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 bg-[#0E0E0E]">
              <button
                onClick={() => onAdd(newTextElement({ fontFamily: f.family, text: f.family, fontSize: 48, height: 70 }))}
                className="flex-1 text-left text-sm truncate"
                style={{ fontFamily: `'${f.family}', sans-serif` }}
                title="Click to add to canvas"
              >
                {f.family}
              </button>
              <button onClick={() => remove(f.id)} data-testid={`delete-font-${f.id}`} className="p-1 rounded text-zinc-500 hover:text-red-400 hover:bg-white/10 transition-colors" title="Remove font">
                <Trash size={15} />
              </button>
            </div>
          ))}
        </div>
      )}
      <p className="text-[10px] text-zinc-500 mb-6">Upload .ttf, .otf, .woff or .woff2 files you're licensed to use. They become available in every font picker.</p>

      <SectionLabel>Font styles</SectionLabel>
      <div className="grid grid-cols-2 gap-2">
        {customFonts.map((f) => (
          <button
            key={f}
            onClick={() => onAdd(newTextElement({ fontFamily: f, text: f, fontSize: 48, height: 70 }))}
            data-testid={`add-font-${f.replace(/\s/g, "-")}`}
            className="px-3 py-2.5 rounded-lg border border-white/10 bg-[#0E0E0E] hover:border-blue-500/40 text-sm truncate transition-all"
            style={{ fontFamily: `'${f}', sans-serif` }}
          >
            {f}
          </button>
        ))}
      </div>
    </div>
  );
}

export function UploadsPanel({ onAdd, onSetAudio, audio }) {
  const [uploads, setUploads] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [embedUrl, setEmbedUrl] = useState("");
  const inputRef = useRef(null);
  const { isPro, openUpgrade } = useSubscription();

  const load = async () => {
    try {
      const res = await api.get("/uploads");
      setUploads(res.data);
    } catch (e) {}
  };
  useEffect(() => { load(); }, []);

  const pollMedia = (fileId) => new Promise((resolve, reject) => {
    let tries = 0;
    const tick = async () => {
      tries += 1;
      try {
        const res = await api.get(`/media/status/${fileId}`);
        if (res.data.status === "ready") return resolve(res.data);
        if (res.data.status === "error") return reject(new Error(res.data.error || "Processing failed"));
      } catch (e) { /* keep polling */ }
      if (tries >= 120) return reject(new Error("Timed out while processing"));
      setTimeout(tick, 2500);
    };
    tick();
  });

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (inputRef.current) inputRef.current.value = "";
    if (!file) return;
    const type = file.type || "";
    const isImage = type.startsWith("image");
    const isMedia = type.startsWith("video") || type.startsWith("audio") ||
      /\.(mp4|mov|m4v|avi|webm|mkv|mp3|wav|m4a|aac|ogg|flac)$/i.test(file.name);

    if (isImage) {
      setUploading(true);
      try {
        const form = new FormData();
        form.append("file", file);
        const res = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
        await load();
        onAdd(newImageElement(fileUrl(res.data.storage_path)));
        toast.success("Uploaded");
      } catch (err) {
        toast.error("Upload failed");
      } finally { setUploading(false); }
      return;
    }

    if (!isMedia) { toast.error("Unsupported file type"); return; }
    if (!isPro) { openUpgrade("Video & audio uploads are a Pro feature. Upgrade to add motion and sound to your flyers."); return; }

    const isAudio = type.startsWith("audio") || /\.(mp3|wav|m4a|aac|ogg|flac)$/i.test(file.name);
    setProcessing(true);
    const tid = toast.loading(isAudio ? "Compressing audio…" : "Compressing video to WebM…");
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post("/media/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      const ready = await pollMedia(res.data.id);
      await load();
      const url = fileUrl(ready.storage_path);
      if (isAudio) {
        onSetAudio && onSetAudio({ storage_path: ready.storage_path, filename: file.name, duration: ready.duration, loop: true, volume: 1 });
        toast.success("Background audio added", { id: tid });
      } else {
        onAdd(newVideoElement(url, { duration: ready.duration }));
        toast.success("Video added to canvas", { id: tid });
      }
    } catch (err) {
      if (err.response?.status === 402) openUpgrade(err.response?.data?.detail || "Upgrade to upload video & audio.");
      else toast.error(err.message || "Processing failed", { id: tid });
      toast.dismiss(tid);
    } finally { setProcessing(false); }
  };

  const addEmbed = () => {
    if (!isPro) { openUpgrade("Embeds (YouTube, Facebook, links) are a Pro feature."); return; }
    const parsed = parseEmbed(embedUrl);
    if (!parsed) { toast.error("Paste a valid YouTube, Facebook, or video link"); return; }
    onAdd(newEmbedElement(parsed.embedType, parsed.url, parsed.videoId ? { videoId: parsed.videoId } : {}));
    setEmbedUrl("");
    toast.success(`${parsed.embedType === "url" ? "Video" : parsed.embedType} embed added`);
  };

  const mediaItems = uploads.filter((u) => {
    const k = u.kind || "";
    const t = u.content_type || "";
    return k === "video" || k === "audio" || t.startsWith("video") || t.startsWith("audio") || k === "upload" || t.startsWith("image");
  });

  return (
    <div className="p-4 custom-scrollbar overflow-y-auto h-full">
      <SectionLabel>Upload media</SectionLabel>
      <input ref={inputRef} type="file" accept="image/*,video/*,audio/*" className="hidden" onChange={handleFile} data-testid="upload-input" />
      <Button
        onClick={() => inputRef.current?.click()}
        disabled={uploading || processing}
        data-testid="upload-btn"
        className="w-full mb-1.5 bg-blue-600 hover:bg-blue-500 text-white"
      >
        {(uploading || processing) ? <Spinner className="animate-spin mr-2" size={18} /> : <UploadSimple weight="bold" size={18} className="mr-2" />}
        {processing ? "Processing…" : "Upload image, video or audio"}
      </Button>
      <p className="text-[10px] text-zinc-500 mb-4">
        Images are free. <span className="text-amber-300 font-medium">Pro:</span> video (MP4/MOV/WebM, auto-compressed to 720p WebM) & audio (MP3). Max 120MB.
      </p>

      {/* Embed link */}
      <div className="mb-4 rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid="embed-section">
        <p className="text-xs font-semibold text-zinc-300 mb-2 flex items-center gap-1.5"><LinkSimple size={14} /> Embed a link {!isPro && <Crown weight="fill" size={11} className="text-amber-300" />}</p>
        <div className="flex gap-2">
          <input
            value={embedUrl}
            onChange={(e) => setEmbedUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addEmbed()}
            placeholder="YouTube, Facebook or video URL"
            data-testid="embed-input"
            className="flex-1 h-9 rounded-lg bg-[#0E0E0E] border border-white/10 px-2.5 text-xs text-white placeholder:text-zinc-600 outline-none focus:border-blue-500/60"
          />
          <button onClick={addEmbed} data-testid="embed-add-btn" className="h-9 px-3 rounded-lg bg-white/10 hover:bg-white/20 text-xs font-semibold transition-colors">Add</button>
        </div>
      </div>

      {/* Current background audio track */}
      {audio && audio.storage_path && (
        <div className="mb-4 rounded-lg border border-blue-500/30 bg-blue-500/5 p-3" data-testid="audio-track-chip">
          <div className="flex items-center gap-2">
            <MusicNotes weight="fill" size={16} className="text-blue-400 shrink-0" />
            <span className="text-xs text-zinc-200 truncate flex-1">{audio.filename || "Background audio"}</span>
            <button onClick={() => onSetAudio && onSetAudio(null)} data-testid="audio-remove-btn" className="text-zinc-400 hover:text-red-400"><Trash size={14} /></button>
          </div>
          <p className="text-[10px] text-zinc-500 mt-1">Plays in the editor & live share link.</p>
        </div>
      )}

      <DriveImport onAdd={onAdd} onImported={load} />

      <div className="grid grid-cols-2 gap-3 mt-3">
        {mediaItems.map((u) => {
          const t = u.content_type || "";
          const isVid = t.startsWith("video") || u.kind === "video";
          const isAud = t.startsWith("audio") || u.kind === "audio";
          const url = fileUrl(u.storage_path);
          const proc = u.status === "processing";
          if (isAud) {
            return (
              <button
                key={u.id}
                onClick={() => !proc && onSetAudio && onSetAudio({ storage_path: u.storage_path, filename: u.original_filename, duration: u.duration, loop: true, volume: 1 })}
                data-testid={`upload-item-${u.id}`}
                className="relative aspect-square rounded-lg overflow-hidden border border-white/10 hover:border-blue-500/60 transition-all bg-[#0E0E0E] flex flex-col items-center justify-center gap-1.5 p-2"
              >
                {proc ? <Spinner className="animate-spin text-blue-400" size={20} /> : <MusicNotes weight="fill" size={22} className="text-blue-400" />}
                <span className="text-[9px] text-zinc-400 truncate w-full text-center">{proc ? "Processing…" : (u.original_filename || "Audio")}</span>
              </button>
            );
          }
          return (
            <button
              key={u.id}
              onClick={() => !proc && onAdd(isVid ? newVideoElement(url, { duration: u.duration }) : newImageElement(url))}
              data-testid={`upload-item-${u.id}`}
              className="relative aspect-square rounded-lg overflow-hidden border border-white/10 hover:border-blue-500/60 transition-all bg-[#0E0E0E]"
            >
              {proc ? (
                <div className="w-full h-full flex flex-col items-center justify-center gap-1.5"><Spinner className="animate-spin text-blue-400" size={20} /><span className="text-[9px] text-zinc-400">Processing…</span></div>
              ) : isVid ? (
                <>
                  <video src={url} muted loop autoPlay playsInline crossOrigin="anonymous" className="w-full h-full object-cover" />
                  <span className="absolute top-1 right-1 bg-black/70 rounded px-1 py-0.5 text-[9px] font-semibold flex items-center gap-0.5"><FilmSlate size={10} /> WebM</span>
                </>
              ) : (
                <img src={url} alt={u.original_filename} crossOrigin="anonymous" className="w-full h-full object-cover" />
              )}
            </button>
          );
        })}
      </div>
      {mediaItems.length === 0 && (
        <div className="text-center text-zinc-500 text-sm py-8">
          <ImageIcon size={32} className="mx-auto mb-2 opacity-50" />
          No uploads yet
        </div>
      )}
    </div>
  );
}

export function AIPanel({ onAdd }) {
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const { sub, refresh, openUpgrade } = useSubscription();

  const used = sub?.ai_used ?? 0;
  const limit = sub?.ai_limit ?? 5;
  const remaining = Math.max(0, limit - used);

  const generate = async () => {
    if (!prompt.trim()) return;
    setLoading(true);
    try {
      const res = await api.post("/ai/generate-image", { prompt });
      const url = fileUrl(res.data.storage_path);
      setResults((prev) => [url, ...prev]);
      toast.success("Image generated");
      refresh();
    } catch (e) {
      if (e.response?.status === 402) {
        openUpgrade(e.response?.data?.detail || "You've reached your AI image limit.");
      } else {
        toast.error(e.response?.data?.detail || "Generation failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 custom-scrollbar overflow-y-auto h-full">
      <SectionLabel>AI image generation</SectionLabel>
      <div className="flex items-center justify-between mb-3 px-2.5 py-2 rounded-lg bg-white/[0.03] border border-white/10" data-testid="ai-usage-meter">
        <span className="text-xs text-zinc-400">{remaining} of {limit} left this month</span>
        {!sub?.is_pro && (
          <button onClick={() => openUpgrade("Upgrade to Pro for 100 AI images a month.")} data-testid="ai-upgrade-link" className="text-xs font-semibold text-amber-300 hover:text-amber-200 inline-flex items-center gap-1">
            <Crown weight="fill" size={12} /> Get more
          </button>
        )}
      </div>
      <textarea
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        placeholder="A vibrant abstract background with neon gradients…"
        data-testid="ai-prompt-input"
        rows={3}
        className="w-full rounded-lg bg-[#0E0E0E] border border-white/10 p-3 text-sm text-white placeholder:text-zinc-600 focus:border-blue-500/60 outline-none resize-none mb-3"
      />
      <Button onClick={generate} disabled={loading || !prompt.trim()} data-testid="ai-generate-btn" className="w-full bg-gradient-to-r from-blue-600 to-fuchsia-600 hover:opacity-90 text-white">
        {loading ? <Spinner className="animate-spin mr-2" size={18} /> : <MagicWand weight="fill" size={18} className="mr-2" />}
        {loading ? "Generating…" : "Generate image"}
      </Button>
      {loading && <p className="text-xs text-zinc-500 mt-2 text-center">This can take up to a minute.</p>}
      <div className="grid grid-cols-2 gap-3 mt-4">
        {results.map((src, i) => (
          <button
            key={i}
            onClick={() => onAdd(newImageElement(src))}
            data-testid={`ai-result-${i}`}
            className="group aspect-square rounded-lg overflow-hidden border border-white/10 hover:border-blue-500/60 transition-all relative"
          >
            <img src={src} alt="ai" crossOrigin="anonymous" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
              <Plus weight="bold" size={20} />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
